import {
  FinfoValuesDTO,
  MgSupplierItemSearchResult2DTO,
  PriceChannel,
  PriceSuggestionDTO,
  SupplierDTO,
  SupplierItemPriceDTO,
} from '../../dto';
import { DiscountGroup } from '../DiscountGroup';
import { PriceSuggestionType, Unit } from '../price';
import { ProductGroupDTO } from '../../dto/mg/ProductGroupDTO';

interface SupplierItemProductGroupProps {
  groupNumber: string;
  name: string;
}

export class SupplierItemProductGroup {
  groupNumber: string;
  name: string;

  constructor({ groupNumber, name }: SupplierItemProductGroupProps) {
    this.groupNumber = groupNumber;
    this.name = name;
  }

  static fromDto = (dto: ProductGroupDTO): SupplierItemProductGroup => {
    return new SupplierItemProductGroup(dto);
  };
}

export interface FinfoValuesProps {
  finfoNumber: number;
  finfoSupplierId?: number;
}

export class FinfoValues {
  finfoNumber: number;
  finfoSupplierId?: number;

  constructor({ finfoNumber, finfoSupplierId }: FinfoValuesProps) {
    this.finfoNumber = finfoNumber;
    this.finfoSupplierId = finfoSupplierId;
  }

  static fromDto = (dto: FinfoValuesDTO): FinfoValues => {
    return new FinfoValues(dto);
  };
}

export interface SupplierProps {
  id: number;
  name: string;
}

export class Supplier {
  id: number;
  name: string;

  constructor({ id, name }: SupplierProps) {
    this.id = id;
    this.name = name;
  }

  static fromDto = (dto: SupplierDTO): Supplier => {
    return new Supplier(dto);
  };
}

export interface PriceProps {
  value: number;
  currency: string;
  vatFactor: number;
}

export class Price {
  value: number;
  currency: string;
  vatFactor: number;

  constructor({ value, currency, vatFactor }: PriceProps) {
    this.value = value;
    this.currency = currency;
    this.vatFactor = vatFactor;
  }

  static fromDto = (dto: SupplierItemPriceDTO): Price => {
    return new Price(dto);
  };
}

export interface PriceSuggestionProps {
  channel: PriceChannel;
  validFrom: string;
  validTo: string;
  priceWithVat: number;
  priceWithoutVat: number;
  unit: Unit;
  priceSuggestionType: PriceSuggestionType;
}

export class PriceSuggestion {
  channel: PriceChannel;
  validFrom: string;
  validTo: string;
  priceWithVat: number;
  priceWithoutVat: number;
  unit: Unit;
  priceSuggestionType: PriceSuggestionType;

  constructor({
    channel,
    validFrom,
    validTo,
    priceWithVat,
    priceWithoutVat,
    unit,
    priceSuggestionType,
  }: PriceSuggestionProps) {
    this.channel = channel;
    this.validFrom = validFrom;
    this.validTo = validTo;
    this.priceWithVat = priceWithVat;
    this.priceWithoutVat = priceWithoutVat;
    this.unit = unit;
    this.priceSuggestionType = priceSuggestionType;
  }

  static fromDto = ({
    channel,
    validFrom,
    validTo,
    price,
    unit,
    priceSuggestionType,
  }: PriceSuggestionDTO): PriceSuggestion => {
    return new PriceSuggestion({
      channel,
      validFrom,
      validTo,
      priceWithVat: price,
      priceWithoutVat: price / 1.25,
      unit: Unit.fromDto(unit),
      priceSuggestionType,
    });
  };
}

export interface MgSupplierItemSearchResult2Props {
  productGroup: SupplierItemProductGroup;
  finfoValues?: FinfoValues;
  mgItemNumber: number;
  mgSupplierItemId: number;
  primaryText: string;
  secondaryText?: string;
  isMainSupplier: boolean;
  isInAssortment: boolean;
  discountGroup?: DiscountGroup;
  supplier: Supplier;
}

export class MgSupplierItemSearchResult2 {
  productGroup: SupplierItemProductGroup;
  finfoValues?: FinfoValues;
  mgItemNumber: number;
  mgSupplierItemId: number;
  primaryText: string;
  secondaryText?: string;
  isMainSupplier: boolean;
  isInAssortment: boolean;
  discountGroup?: DiscountGroup;
  supplier: Supplier;

  constructor({
    productGroup,
    finfoValues,
    mgItemNumber,
    mgSupplierItemId,
    primaryText,
    secondaryText,
    isMainSupplier,
    isInAssortment,
    discountGroup,
    supplier,
  }: MgSupplierItemSearchResult2Props) {
    this.productGroup = productGroup;
    this.finfoValues = finfoValues;
    this.mgItemNumber = mgItemNumber;
    this.mgSupplierItemId = mgSupplierItemId;
    this.primaryText = primaryText;
    this.secondaryText = secondaryText;
    this.isMainSupplier = isMainSupplier;
    this.isInAssortment = isInAssortment;
    this.discountGroup = discountGroup;
    this.supplier = supplier;
  }

  static fromDto = ({
    productGroup,
    finfoValues,
    mgItemNumber,
    mgSupplierItemId,
    primaryText,
    secondaryText,
    isMainSupplier,
    isInAssortment,
    discountGroup,
    supplier,
  }: MgSupplierItemSearchResult2DTO): MgSupplierItemSearchResult2 => {
    return new MgSupplierItemSearchResult2({
      productGroup: SupplierItemProductGroup.fromDto(productGroup),
      finfoValues: finfoValues ? FinfoValues.fromDto(finfoValues) : undefined,
      mgItemNumber,
      mgSupplierItemId,
      primaryText,
      secondaryText,
      isMainSupplier,
      isInAssortment,
      discountGroup: discountGroup ? DiscountGroup.fromDto(discountGroup) : undefined,
      supplier: Supplier.fromDto(supplier),
    });
  };
}
